<template>
  <div :class="$style.container">
    <UserStateIndicator
      :item="item"
    />
    <div
      v-if="showSendEmailButton"
      :class="$style.activationEmailStatusContainer"
    >
      <hr :class="$style.separator">
      <h4>Send activation email to user</h4>
      <FlatActionButton
        :action="openActivationEmailModal"
        text="Send"
      />
    </div>
    <hr :class="$style.separator">
    <h4
      :class="$style.securityContainer"
      :data-test-id="$testId('securityContainer')"
    >
      Security Status
    </h4>
    <ul :class="$style.list">
      <li
        v-if="displayPasswordStatus"
        :class="$style.statusItem"
      >
        <Status v-bind="passwordStatusProps" />
      </li>
      <li
        v-if="userId"
        :class="$style.statusItem"
      >
        <Status v-bind="mfaStatusProps" />
      </li>
      <li
        v-if="userId"
        :class="$style.statusItem"
      >
        <Status v-bind="mfaPushStatusProps" />
      </li>
      <li
        v-if="userId"
        :class="$style.statusItem"
      >
        <Status v-bind="webAuthnStatusProps" />
      </li>
    </ul>
    <UserResetMFAModal
      v-model="resetMfaModal.visible"
      :item="item"
    />
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { capitalize, get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import { systemUserMessageText } from 'client/systemusers/constants/SystemUserConstants';
import ActivationEmailConfirmationModal from 'client/systemusers/Modals/ActivationEmailConfirmationModal.vue';
import DateUtil from 'client/util/DateUtil';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import Status from 'components/Status/Status.vue';
import UnlockUserModal from 'components/Modal/UnlockUserModal.vue';
import UserItemStates from 'components/mixins/UserItemStates';
import UserResetMFAModal from 'components/Modal/mfaModals/UserResetMFAModal.vue';
import UserStateIndicator from 'client/systemusers/UserDetails/UserStateIndicator.vue';

const {
  ItemTypeConsts,
  ModuleConsts,
  StatusColorNames,
  StoreModelMap,
  StoreModuleMap,
} = Constants;
const { error, success } = Constants.default;
const { accountStates, mfaStates, passwordStates } = ModuleConsts.systemUser;

const pushMfaUserEndpointsModule = StoreModuleMap[ItemTypeConsts.pushMfaUserEndpoints];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const systemUserModel = StoreModelMap[ItemTypeConsts.systemUser];
const systemUserWebAuthKeysModel = StoreModelMap[ItemTypeConsts.systemUserWebAuthnKeys];
const { activated, staged, suspended } = ModuleConsts.systemUser.accountStates;

export default {
  name: 'UserStatus',

  components: {
    FlatActionButton,
    Status,
    UserResetMFAModal,
    UserStateIndicator,
  },

  ModalEventBus,

  mixins: [
    UserItemStates,
  ],

  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    resetMfaModal: {
      visible: false,
    },
  }),

  computed: {
    ...mapGetters(systemUserModel, ['displayName']),

    ...mapState(settingsModule, ['passwordPolicy']),

    ...mapState(systemUserWebAuthKeysModel, {
      webAuthnKeys: state => state.item.webAuthnKeys,
    }),

    ...mapState(pushMfaUserEndpointsModule, {
      pushEndpoints: state => state.endpoints,
    }),

    /**
     * @return {Boolean} [whether or not to display the password status component]
     */
    displayPasswordStatus() {
      const { passwordStatusMap, passwordStatus } = this;
      return passwordStatusMap.has(passwordStatus);
    },

    /**
     * Helper method which determines the subtext of the password status.
     * @return {String} [subtext that contains the formated date. Defaults to undefined
     *                  if the user item object does not contain a password expiration date]
     */
    formattedPasswordExpiration() {
      const passwordExpirationDate = this.item.password_expiration_date;
      return passwordExpirationDate
        ? `${this.expireText(passwordExpirationDate)} ${DateUtil.formatDate(passwordExpirationDate)}`
        : undefined;
    },

    isSambaUser() {
      return this.item.samba_service_user;
    },

    mfaPushStatusProps() {
      const status = get(this.pushEndpoints[0], 'status', mfaStates.inactive);

      return this.jcProtectStatusMap.get(status);
    },

    /**
     * Generates the mfa status props. Merges in the mfaStatusSubtext object and adds the
     * menuSections object defined in the created method.
     * @return {Object} [The user's mfa status props]
     */
    mfaStatusProps() {
      const {
        mfaMenuSections, mfaStatus, mfaStatusMap,
      } = this;

      // No dropdown menu needed when TOTP is inactive
      const menuSections = mfaStatus === mfaStates.inactive
        ? null
        : mfaMenuSections;

      // The template ensures there is always a valid mfa status
      const { color, label } = mfaStatusMap.get(mfaStatus);

      return {
        color,
        label,
        menuSections,
        ...this.mfaStatusSubtext,
      };
    },

    /**
     * Displays the expiration date of the mfa enrollment period and if the enrollment period
     * has expired or not.
     * @return {Object} [subtext props for mfa subtext. Defaults to an empty Object]
     */
    mfaStatusSubtext() {
      const { mfaStatus } = this;
      const { enrollment, enrollmentExpired } = mfaStates;
      const { critical, warning } = StatusColorNames;
      const exclusionUntil = get(this.item, 'mfa.exclusionUntil', '');

      let subtext;
      let subtextColor;

      if (mfaStatus === enrollment
        && this.item.state === accountStates.staged) {
        subtextColor = warning;
        subtext = 'Enrollment begins when user state is active.';
      }

      if ((mfaStatus === enrollment || mfaStatus === enrollmentExpired) && exclusionUntil) {
        subtextColor = DateUtil.hasExpired(exclusionUntil) ? critical : warning;
        subtext = `${this.expireText(exclusionUntil)} ${DateUtil.formatDate(exclusionUntil)}`;
      }
      return subtext ? { subtext, subtextColor } : {};
    },

    /**
     * @todo: When the new aside panel is released make this a getter in the SystemUserModel.
     * This uses the settings module in order to determine if a user's password expires as well
     * as the item.
     */
    passwordNeverExpires() {
      const { enablePasswordExpirationInDays } = this.passwordPolicy;
      return this.item.password_never_expires || (enablePasswordExpirationInDays === false);
    },

    /**
     * Generates the password status props.
     * @return {Object} [The user's password status props]
     */
    passwordStatusProps() {
      const {
        forceChangeMenuSections,
        passwordLockedMenuSections,
        passwordStatus,
        passwordStatusMap,
      } = this;
      const { color, label } = passwordStatusMap.get(passwordStatus);
      const { locked } = passwordStates;
      const props = {
        color,
        label,
        ...this.passwordStatusSubtext,
      };

      // If the user is externally managed and not locked out then display a unique label
      if (this.item.externally_managed && passwordStatus !== locked) {
        props.label = 'password externally managed';
      }

      if (!this.isSambaUser && passwordStatus === locked) {
        const forceChangeMenuItem = forceChangeMenuSections[0].menuItems[0];
        const unlockMenuItem = passwordLockedMenuSections[0].menuItems[0];
        props.menuSections = [{ menuItems: [forceChangeMenuItem, unlockMenuItem] }];
      } else if (this.isSambaUser && passwordStatus === locked) {
        props.menuSections = passwordLockedMenuSections;
      } else if (!this.isSambaUser && this.item._id && !this.item.password_expired) {
        props.menuSections = forceChangeMenuSections;
      }

      return props;
    },

    /**
     * Determines the text for the subtext prop for the status. Also uses the
     * formattedPasswordExpiration function in order to generate the text.
     * If the password does not expire then it just displays the password expired text unless
     * the user is expired (force change at log in) - then it displays no text.
     *
     * This should be merged into the passwordStatus props object.
     *
     * @return {Object} [subtext props for password subtext. Defaults to an empty Object]
     */
    passwordStatusSubtext() {
      const { enableDaysAfterExpirationToSelfRecover } = this.passwordPolicy;
      const { passwordNeverExpires, passwordStatus, passwordStatusMap } = this;
      const { active, expired } = passwordStates;

      let subtext;

      if (this.isRestrictedField(this.item)('password')) return '';

      if (passwordStatus === active) {
        subtext = passwordNeverExpires
          ? passwordStatusMap.get('never expires').label : this.formattedPasswordExpiration;
      }

      if (passwordStatus === expired) {
        subtext = enableDaysAfterExpirationToSelfRecover
          ? passwordStatusMap.get('force change').label : this.formattedPasswordExpiration;
      }

      return subtext ? { subtext, subtextColor: this.passwordSubtextColor } : {};
    },

    /**
     * Determines color of the subtext by using the DateUtil function to check the number of days
     * left until the expiration date expires. Defaults to the info color. If the user's password is
     * expired and we're displaying the 'must change at next login' message, use info color, not
     * critical per UX.
     * @return {String} [A StatusColorName]
     */
    passwordSubtextColor() {
      const { enableDaysAfterExpirationToSelfRecover } = this.passwordPolicy;
      const { item, passwordExpirationNumDays } = this;
      const { critical, info, warning } = StatusColorNames;
      let color = info;

      if (enableDaysAfterExpirationToSelfRecover) {
        color = info;
      } else if (passwordExpirationNumDays > 1 && passwordExpirationNumDays <= 7) {
        color = warning;
      } else if (passwordExpirationNumDays <= 1 || item.password_expired) {
        color = critical;
      }
      return color;
    },

    showChangeMenu() {
      return !!this.item._id;
    },

    showSendEmailButton() {
      return this.item._id
        && this.item.state === accountStates.activated
        && this.passwordStatus === passwordStates.pending;
    },

    userId() {
      return this.$route.params.id;
    },

    webAuthnStatusProps() {
      const status = this.webAuthnKeys.length ? mfaStates.active : mfaStates.inactive;
      return this.webAuthnStatusMap.get(status);
    },
  },

  created() {
    // Helper function to make a simple object that statuses map to, for clarity
    const mapValue = (color, label) => ({ color, label });

    /**
     * The following maps are used to get values associated with user states.
     * It assists in creating the props passed to the status components. The statuses will render
     * if and only if the status matches the keys in these maps. See the display*Status
     * functions in the computed properties below.
     */

    this.mfaStatusMap = new Map([
      [mfaStates.active, mapValue(StatusColorNames.active, 'TOTP MFA Active')],
      [mfaStates.inactive, mapValue(StatusColorNames.inactive, 'TOTP MFA Inactive')],
      [mfaStates.enrollment, mapValue(StatusColorNames.warning, 'in TOTP MFA enrollment')],
      [mfaStates.enrollmentExpired, mapValue(StatusColorNames.critical, 'TOTP MFA enrollment expired')],
    ]);

    this.webAuthnStatusMap = new Map([
      [mfaStates.active, mapValue(StatusColorNames.active, 'WebAuthn Active')],
      [mfaStates.inactive, mapValue(StatusColorNames.inactive, 'WebAuthn Inactive')],
    ]);

    this.passwordStatusMap = new Map([
      [passwordStates.active, mapValue(StatusColorNames.active, 'Password Active')],
      [passwordStates.expired, mapValue(StatusColorNames.critical, 'Password Expired')],
      [passwordStates.locked, mapValue(StatusColorNames.critical, 'Account Locked Out')],
      [passwordStates.pending, mapValue(StatusColorNames.warning, 'Password Pending')],
      ['force change', mapValue(StatusColorNames.info, 'password must be changed at login')],
      ['never expires', mapValue(StatusColorNames.info, 'password never expires')],
    ]);

    this.jcProtectStatusMap = new Map([
      [mfaStates.active, mapValue(StatusColorNames.active, 'JumpCloud Protect Active')],
      [mfaStates.pending, mapValue(StatusColorNames.warning, 'JumpCloud Protect Pending')],
      [mfaStates.inactive, mapValue(StatusColorNames.inactive, 'JumpCloud Protect Inactive')],
    ]);

    const forceChangeMenuItem = { label: 'Force Password Change', action: this.handleExpire };
    const resetMfaMenuItem = { label: 'Reset TOTP MFA', action: this.resetMfa };
    const unlockMenuItem = { label: 'Unlock User Account', action: this.handleUnlock };

    this.forceChangeMenuSections = [
      { menuItems: [forceChangeMenuItem] },
    ];

    this.mfaMenuSections = [
      { menuItems: [resetMfaMenuItem] },
    ];

    this.passwordLockedMenuSections = [
      { menuItems: [unlockMenuItem] },
    ];

    if (this.userId) {
      this.$store.dispatch(
        `${pushMfaUserEndpointsModule}/getDeviceEndpoints`,
        this.userId,
      );
    }

    const mapStateValue = (noun, verb, result) => ({ noun, verb, result });
    this.stateChangeMap = new Map([
      [activated, mapStateValue('suspend', 'suspending', 'suspended')],
      [staged, mapStateValue('activate', 'activating', 'activated')],
      [suspended, mapStateValue('activate', 'activating', 'activated')],
    ]);
  },

  methods: {
    ...mapActions(systemUserModel, [
      'expireUser',
      'unlockUser',
      'updateItem',
      'updatePasswordPolicy',
    ]),

    ...mapGetters(systemUserModel, [
      'isRestrictedField',
    ]),

    changeUserStateValues(menuItemsArray, action) {
      menuItemsArray.push({
        label: `${capitalize(action)}`,
        action: () => this.handleChangeState(action),
      });
    },

    async continueExpire() {
      // Update the password policy for the org first; if that fails, don't expire the user
      try {
        this.updateItem({ password_expired: true });
        await this.updatePasswordPolicy();
        await this.expireUser(this.item._id);
        notify(success, `The password for ${this.displayName} has been successfully expired.`);
      } catch {
        this.updateItem({ password_expired: false });
        notify(error, `Error expiring the password for ${this.displayName}.`);
      }
    },

    /**
     * Used in the formattedPasswordExpiration and mfaStubtext computed methods to assist in
     * generating the status subtext that display a date that can or has expired.
     * @param  {String} timestamp [the timestamp from the user object]
     * @return {String}           [the correct use of the word expires]
     */
    expireText(timestamp) {
      return DateUtil.hasExpired(timestamp) ? 'expired' : 'expires';
    },

    // Expires the user's password
    handleExpire() {
      const componentProps = {

        handleContinue: () => {
          ModalEventBus.close();
          this.continueExpire();
        },

        infoText: !this.passwordPolicy.enableDaysAfterExpirationToSelfRecover ? systemUserMessageText.forcePasswordInfoText : '',
      };

      ModalEventBus.open({
        component: 'ForcePasswordChangeModal',
        componentProps,
        preventCloseOnOverlayClick: true,
      });
    },

    // Renders the unlock user modal
    handleUnlock() {
      ModalEventBus.open({
        component: UnlockUserModal,
        componentProps: {
          action: () => {
            ModalEventBus.close();

            this.unlockUser()
              .then(() => {
                notify(success, `The account for ${this.displayName} has been successfully unlocked.`);
              })
              .catch(() => {
                notify(error, `Error unlocking the account for ${this.displayName}.`);
              });
          },
          item: this.item,
        },
        showClose: false,
      });
    },

    openActivationEmailModal() {
      this.$options.ModalEventBus.open({
        component: ActivationEmailConfirmationModal,
        componentProps: {
          defaultInputValue: this.item.alternateEmail || this.item.email,
          closeAsideOnSubmit: false,
        },
      });
    },

    /**
     * Renders the reset mfa modal. Passed to the menuSections prop to the MFA status component.
     */
    resetMfa() {
      this.resetMfaModal.visible = true;
    },
  },
};
</script>

<style module>
.container {
  min-width: 160px;
  padding-top: var(--jc-spacer);
}

.activationEmailStatusContainer {
  margin-bottom: 2rem;
}

.securityContainer {
  margin-bottom: 5px;
}

.stateBadge {
  display: block;
  height: 19px;
  margin: 0 0 10px 0;
}

.list {
  display: inline-block;
  margin-top: 2px;
  padding-inline-start: 20px;
  text-align: left;
}

.statusItem {
  list-style-type: none;
  padding: 6px 0;
}

.separator.separator {
  border: 0;
  border-bottom: var(--jc-border-width) solid var(--jc-border-color-medium);
  margin-top: var(--jc-spacer);
}
</style>
